import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  Timestamp,
  type Unsubscribe,
  type QuerySnapshot,
  type DocumentData,
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';
import { ownedStoragePath, storagePathFromDownloadURL, uploadImage } from './storage';
import { MAX_PHOTO_CAPTION_LENGTH, type PlantPhoto } from '../types/photo';

/** File extension every gallery upload is stored under (PhotoCapture emits JPEG). */
const PHOTO_FILE_EXTENSION = 'jpg';

type Result<T = unknown> = ({ success: true } & T) | { success: false; error: string };

function photosCollection(userId: string, plantId: string) {
  return collection(db, 'users', userId, 'plants', plantId, 'photos');
}

function photoDoc(userId: string, plantId: string, photoId: string) {
  return doc(db, 'users', userId, 'plants', plantId, 'photos', photoId);
}

function plantDoc(userId: string, plantId: string) {
  return doc(db, 'users', userId, 'plants', plantId);
}

function photoStoragePath(userId: string, plantId: string, photoId: string): string {
  return `users/${userId}/plants/${plantId}/${photoId}.${PHOTO_FILE_EXTENSION}`;
}

function normalizeCaption(caption: string | null | undefined): string | null {
  const trimmed = caption?.trim() ?? '';
  return trimmed ? trimmed.slice(0, MAX_PHOTO_CAPTION_LENGTH) : null;
}

function toPhotos(snapshot: QuerySnapshot<DocumentData>): PlantPhoto[] {
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as PlantPhoto[];
}

/**
 * Subscribe to a plant's photos, oldest first, so the gallery reads
 * left-to-right as a growth timeline.
 */
export function subscribeToPhotos(
  userId: string,
  plantId: string,
  callback: (photos: PlantPhoto[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const q = query(photosCollection(userId, plantId), orderBy('createdAt', 'asc'));

  return onSnapshot(
    q,
    (snapshot) => callback(toPhotos(snapshot)),
    (error) => {
      console.error('Photos subscription error:', error);
      onError?.(error);
    }
  );
}

/**
 * Add a photo to a plant's gallery.
 *
 * Two behaviors worth knowing about:
 *
 * 1. **Lazy backfill.** Plants created before the gallery existed have a
 *    `photoURL` but no photo docs. The first write to such a plant's gallery
 *    also creates a photo doc for that existing image, marked primary and
 *    dated to the plant's creation, so no migration script is needed.
 * 2. **The new photo does NOT become the key photo** unless it is the plant's
 *    very first. Choosing the key photo is the user's call — auto-promoting the
 *    newest upload would silently change the home grid on every add.
 */
export async function addPhoto(
  userId: string,
  plantId: string,
  file: File,
  caption?: string | null
): Promise<Result<{ id: string }>> {
  try {
    const [existingSnapshot, plantSnapshot] = await Promise.all([
      getDocs(photosCollection(userId, plantId)),
      getDoc(plantDoc(userId, plantId)),
    ]);

    if (!plantSnapshot.exists()) {
      return { success: false, error: 'Could not find that plant.' };
    }

    const newPhotoRef = doc(photosCollection(userId, plantId));
    const storagePath = photoStoragePath(userId, plantId, newPhotoRef.id);
    const url = await uploadImage(storagePath, file);

    const plant = plantSnapshot.data();
    const legacyPhotoURL = typeof plant.photoURL === 'string' ? plant.photoURL : '';
    const galleryIsEmpty = existingSnapshot.empty;
    const isFirstPhoto = galleryIsEmpty && !legacyPhotoURL;

    const batch = writeBatch(db);

    if (galleryIsEmpty && legacyPhotoURL) {
      batch.set(doc(photosCollection(userId, plantId)), {
        url: legacyPhotoURL,
        // Pre-gallery uploads never recorded their Storage path, so recover it
        // from the download URL. Without this the file would be unreachable
        // once a different photo becomes primary and the plant's `photoURL`
        // stops pointing at it. Empty when the legacy photo is a reference
        // image we don't own.
        storagePath:
          ownedStoragePath(
            userId,
            (typeof plant.photoStoragePath === 'string' ? plant.photoStoragePath : null) ??
              storagePathFromDownloadURL(legacyPhotoURL)
          ) ?? '',
        isPrimary: true,
        caption: null,
        createdAt: plant.createdAt ?? Timestamp.fromMillis(0),
      });
    }

    batch.set(newPhotoRef, {
      url,
      storagePath,
      isPrimary: isFirstPhoto,
      caption: normalizeCaption(caption),
      // Client time rather than serverTimestamp(): a pending server timestamp
      // reads as null locally, and orderBy('createdAt') sorts nulls first, so
      // the new photo would visibly jump from the front of the strip to the end
      // once the write resolved.
      createdAt: Timestamp.now(),
    });

    if (isFirstPhoto) {
      batch.update(plantDoc(userId, plantId), {
        photoURL: url,
        updatedAt: serverTimestamp(),
      });
    }

    await batch.commit();
    return { success: true, id: newPhotoRef.id };
  } catch (error) {
    console.error('Failed to add photo:', error);
    return { success: false, error: 'Could not save photo. Please try again.' };
  }
}

/**
 * Make a photo the plant's key photo.
 *
 * One atomic batch: clear the old primary, set the new one, and mirror its URL
 * onto the plant doc. A partial write would leave the home grid disagreeing
 * with the gallery's key badge, which reads to the user as data loss.
 */
export async function setPrimaryPhoto(
  userId: string,
  plantId: string,
  photoId: string
): Promise<Result> {
  try {
    const snapshot = await getDocs(photosCollection(userId, plantId));
    const target = snapshot.docs.find((d) => d.id === photoId);

    if (!target) {
      return { success: false, error: 'Could not find that photo.' };
    }

    const url = target.data().url as string;
    const batch = writeBatch(db);

    for (const d of snapshot.docs) {
      const shouldBePrimary = d.id === photoId;
      if ((d.data().isPrimary === true) !== shouldBePrimary) {
        batch.update(d.ref, { isPrimary: shouldBePrimary });
      }
    }

    batch.update(plantDoc(userId, plantId), {
      photoURL: url,
      updatedAt: serverTimestamp(),
    });

    await batch.commit();
    return { success: true };
  } catch (error) {
    console.error('Failed to set key photo:', error);
    return { success: false, error: 'Could not set key photo. Please try again.' };
  }
}

/**
 * Update (or clear) a photo's caption.
 */
export async function updatePhotoCaption(
  userId: string,
  plantId: string,
  photoId: string,
  caption: string
): Promise<Result> {
  try {
    await updateDoc(photoDoc(userId, plantId, photoId), {
      caption: normalizeCaption(caption),
    });
    return { success: true };
  } catch (error) {
    console.error('Failed to update caption:', error);
    return { success: false, error: 'Could not save caption. Please try again.' };
  }
}

/**
 * Delete a photo and its Storage object.
 *
 * Guarded twice: the last remaining photo can't be deleted, and neither can the
 * key photo while others exist. `plant.photoURL` pointing at a deleted object
 * would render a broken image everywhere the plant appears, and there is no
 * "plant with no photo" state anywhere in the app.
 */
export async function deletePhoto(
  userId: string,
  plantId: string,
  photoId: string
): Promise<Result> {
  try {
    const snapshot = await getDocs(photosCollection(userId, plantId));
    const target = snapshot.docs.find((d) => d.id === photoId);

    if (!target) {
      return { success: false, error: 'Could not find that photo.' };
    }
    if (snapshot.size <= 1) {
      return {
        success: false,
        error: 'This is the only photo of this plant. Add another before deleting it.',
      };
    }
    if (target.data().isPrimary === true) {
      return {
        success: false,
        error: 'This is the key photo. Choose a different key photo before deleting it.',
      };
    }

    const storagePath = target.data().storagePath as string | undefined;

    // Document first: an orphaned file is invisible, but a document pointing at
    // a missing file renders as a broken image.
    await deleteDoc(target.ref);
    await deletePhotoFile(storagePath);

    return { success: true };
  } catch (error) {
    console.error('Failed to delete photo:', error);
    return { success: false, error: 'Could not delete photo. Please try again.' };
  }
}

/**
 * Delete a photo's Storage object if its path is known. Never throws — a
 * missing or already-deleted file must not fail the operation that called it.
 */
export async function deletePhotoFile(storagePath: string | undefined | null): Promise<void> {
  if (!storagePath) return;
  try {
    await deleteObject(ref(storage, storagePath));
  } catch (error) {
    console.warn('Could not delete photo file:', storagePath, error);
  }
}

/**
 * Delete every photo document and Storage object for a plant.
 * Used by `deletePlant` to cascade.
 */
export async function deleteAllPhotos(userId: string, plantId: string): Promise<void> {
  const snapshot = await getDocs(photosCollection(userId, plantId));
  if (snapshot.empty) return;

  const batch = writeBatch(db);
  snapshot.docs.forEach((d) => batch.delete(d.ref));
  await batch.commit();

  await Promise.all(
    snapshot.docs.map((d) => deletePhotoFile(d.data().storagePath as string | undefined))
  );
}
